// useLapseStatus (Story 10.5, UX-DR16) — derives the lapse-surface phase from the
// resolved entitlement horizon (`validUntil` from GET /billing/subscription-state).
//
// The trigger reads the ENTITLEMENT layer, not Stripe rows (Decision 3): a
// first-year `learnworlds_new` clinician has no billing.subscriptions row — their
// manual-override `valid_until` drives the same countdown. Day math is
// threshold-CROSSING (ceil of the remaining interval + range checks), so a user
// first seen at d=12 still gets the info banner (10.4 precedent).
//
// Never-show guards (AC7): `loading`/unresolved states derive to "none" (a failed
// or offline read must never surface a lapse), `validUntil: null` derives to
// "none" (pre-calibration learners must reach calibration unblocked), and
// `org_seat` ALWAYS derives to "none" — B2B lapse is immediate with no warning
// and no self-renew (UX §13.11).
//
// While a window is active (countdown or expired) the subscription-state query
// re-checks every 30s and on window focus, so a Stripe-side renewal releases the
// banner/modal without a reload (AC5; webhook busts the server cache, seam-10.1).
// Idle users never poll — the interval/focus escalation is gated on the same
// derivation, evaluated against the freshest query data.

import { useEffect, useReducer } from "react";

import {
  mapSubscriptionResponse,
  useSubscriptionState,
  isResolvedSubscription,
  type SubscriptionState,
  type SubscriptionStateResponse,
} from "@/hooks/queries/useSubscriptionState";

export type LapseTier = "info" | "warn" | "danger";

export type LapseStatus =
  | { phase: "none"; validUntil: Date | null }
  | { phase: "countdown"; tier: LapseTier; daysLeft: number; validUntil: Date }
  | { phase: "expired"; validUntil: Date };

const MS_PER_DAY = 86_400_000;

/** AC5 — the user-visible release ceiling for a server-side renewal. */
export const LAPSE_RECHECK_INTERVAL_MS = 30_000;

/**
 * Coarse clock tick while a lapse window is active or approaching. The 30s poll
 * re-FETCHES but an unchanged payload never re-RENDERS (tracked props +
 * structural sharing), so time-driven transitions — tier escalation, the d=14
 * crossing, the daysLeft copy — would otherwise stall in a long-lived idle tab
 * (clinic sessions are exactly that). One cheap interval, no per-second timers,
 * and only for users within the approach window.
 */
export const LAPSE_CLOCK_TICK_MS = 60_000;

/**
 * Pure derivation: subscription state + clock → lapse phase. UTC epoch math
 * (`Date` subtraction), `ceil` for crossing semantics: d=1 means "ends within
 * the next 24h", d=0/negative means the horizon has passed (AC3's
 * `validUntil <= now`).
 */
export function deriveLapseStatus(
  state: SubscriptionState,
  now: Date,
): LapseStatus {
  if (!isResolvedSubscription(state)) {
    return { phase: "none", validUntil: null };
  }
  // org_seat carries NO horizon out: a seat end date must never feed the
  // clock-tick pre-arm (`needsClockTick`) — an org user can never enter
  // countdown/expired, so a near/past seat horizon would arm a permanent
  // 60s re-render interval for nothing (13.1 OrgSeatProvider surfaces this).
  if (state.source === "org_seat" || state.validUntil === null) {
    return { phase: "none", validUntil: null };
  }
  // A malformed `valid_until` string parses to an Invalid Date (NaN epoch) —
  // treat it as no horizon, mirroring the malformed-payload→loading posture,
  // rather than letting NaN fall through the range checks into the info tier.
  if (Number.isNaN(state.validUntil.getTime())) {
    return { phase: "none", validUntil: null };
  }
  const daysLeft = Math.ceil(
    (state.validUntil.getTime() - now.getTime()) / MS_PER_DAY,
  );
  if (daysLeft <= 0) {
    return { phase: "expired", validUntil: state.validUntil };
  }
  // Story 22.3 (AC22) — a claim that is NOT active has nothing to count down
  // to. A reversal leaves a B2C clinician exactly here: Stripe cancels at
  // period end, so the row reads `canceled` while the horizon it carries is
  // still weeks ahead, and the horizon alone would read as "renew within N
  // days" for a subscription that is already over. The horizon is dropped on
  // the way out for the same reason `org_seat` drops it above — a state that
  // can never reach countdown must not arm `needsClockTick`'s 60s interval.
  // Placed BELOW the expired branch deliberately: a genuinely lapsed user is
  // also `isActive: false`, and they keep their modal and lapsed banner.
  if (!state.isActive) {
    return { phase: "none", validUntil: null };
  }
  if (daysLeft > 14) {
    return { phase: "none", validUntil: state.validUntil };
  }
  const tier: LapseTier =
    daysLeft === 1 ? "danger" : daysLeft <= 7 ? "warn" : "info";
  return { phase: "countdown", tier, daysLeft, validUntil: state.validUntil };
}

/** True when the freshest raw payload sits inside a lapse window (countdown/expired). */
function isLapseWindow(data: SubscriptionStateResponse | undefined): boolean {
  return (
    deriveLapseStatus(mapSubscriptionResponse(data), new Date()).phase !== "none"
  );
}

/**
 * True while the clock alone can change the derived phase: any active window,
 * plus the final approach to the d=14 crossing (one day early), so the banner
 * can APPEAR in a long-lived tab — polling only starts once a window is active.
 */
function needsClockTick(status: LapseStatus, now: Date): boolean {
  if (status.phase !== "none") return true;
  if (!status.validUntil) return false;
  return status.validUntil.getTime() - now.getTime() <= 15 * MS_PER_DAY;
}

/**
 * Lapse phase for the current user, with the AC5 re-check wired in: 30s polling
 * (bypasses staleTime by design) and focus refetch escalated to "always" ONLY
 * while a window is active. The baseline fetch always runs once — that is how an
 * idle observer learns there is nothing to watch.
 *
 * Sticky on error (`keepResolvedOnError`): a failed background re-check keeps
 * the last CONFIRMED state, so the non-dismissible modal (AC3) and the banner
 * never blink out on a network blip. The write gates elsewhere do NOT opt in —
 * their fail-open error→loading contract is untouched.
 */
export function useLapseStatus(): LapseStatus {
  const state = useSubscriptionState({
    keepResolvedOnError: true,
    refetchInterval: (query) =>
      isLapseWindow(query.state.data) ? LAPSE_RECHECK_INTERVAL_MS : false,
    refetchOnWindowFocus: (query) =>
      isLapseWindow(query.state.data) ? "always" : true,
  });
  const [, tick] = useReducer((n: number) => n + 1, 0);

  const now = new Date();
  const status = deriveLapseStatus(state, now);

  // Coarse re-derivation clock (see LAPSE_CLOCK_TICK_MS): an unchanged payload
  // produces no render, so time-driven transitions need their own tick.
  const ticking = needsClockTick(status, now);
  useEffect(() => {
    if (!ticking) return;
    const id = setInterval(tick, LAPSE_CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, [ticking]);

  return status;
}
